import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Tooltip, Spin, Tabs, message } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateRightOutlined,
  UndoOutlined,
  DownloadOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  CopyOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  VideoCameraOutlined,
  CustomerServiceOutlined,
  FileUnknownOutlined,
} from "@ant-design/icons";
import { Capacitor } from "@capacitor/core";
import CapacitorPdfViewer from "../../Components/CapacitorPdfViewer";
import * as XLSX from "xlsx";
import { getFullAwsUrl } from "../../helpers/utils";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export interface FilePreviewItem {
  url?: string;
  name?: string;
  type?: string;
  fileObject?: File;
}

export interface CommonFilePreviewModalProps {
  visible?: boolean;
  open?: boolean; // Support both visible and open props for compatibility
  onClose: () => void;

  // Single file preview
  file?: string | File | FilePreviewItem;

  // Multiple files preview
  files?: (string | File | FilePreviewItem)[];
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
}

// Injected styles to keep component self-contained and beautifully customized
const cssStyles = `
  .common-file-preview-modal .ant-modal-content {
    background-color: #ffffff !important;
    color: #333333 !important;
    border-radius: 12px;
    overflow: hidden;
  }
  .common-file-preview-modal .ant-modal-header {
    background-color: #ffffff !important;
    border-bottom: 1px solid #f0f0f0 !important;
    padding: 16px 24px !important;
  }
  .common-file-preview-modal .ant-modal-title {
    color: #333333 !important;
  }
  .common-file-preview-modal .ant-modal-close {
    color: #333333 !important;
  }
  .common-file-preview-modal .ant-modal-close-icon {
    color: #333333 !important;
  }
  .preview-checkered-bg {
    background-image: radial-gradient(#e8e8e8 20%, transparent 20%), radial-gradient(#e8e8e8 20%, transparent 20%);
    background-size: 16px 16px;
    background-position: 0 0, 8px 8px;
    background-color: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    user-select: none;
    border-radius: 8px;
  }
  .excel-preview-table {
    border-collapse: collapse;
    width: 100%;
    font-size: 13px;
    background-color: #ffffff;
    color: #333333;
  }
  .excel-preview-table th {
    background-color: #f5f5f5;
    color: #333333;
    font-weight: bold;
    border: 1px solid #d9d9d9;
    padding: 8px 12px;
    text-align: left;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .excel-preview-table td {
    border: 1px solid #f0f0f0;
    padding: 8px 12px;
    white-space: nowrap;
  }
  .excel-preview-table tr:hover {
    background-color: #fafafa;
  }
  .code-preview-container {
    background-color: #fafafa;
    color: #333333;
    font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.5;
    padding: 16px;
    overflow: auto;
    border-radius: 8px;
    max-height: 60vh;
    border: 1px solid #e8e8e8;
    margin: 0;
  }
  .audio-preview-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #f0f0f0;
    width: 100%;
    max-width: 480px;
    margin: 40px auto;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
  .audio-vinyl-disk {
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, #333 30%, #111 31%, #111 60%, #222 61%, #111 70%, #000 71%);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    border: 4px solid #eaeaea;
  }
  .audio-vinyl-center {
    position: absolute;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #1890ff;
    border: 3px solid #fff;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .audio-disk-spin {
    animation: spin 8s linear infinite;
  }
  .audio-disk-paused {
    animation-play-state: paused;
  }
  @keyframes soundwave {
    0%, 100% { height: 8px; }
    50% { height: 35px; }
  }
  .wave-bar {
    width: 6px;
    height: 10px;
    background-color: #1890ff;
    border-radius: 3px;
    animation: soundwave 1.2s ease-in-out infinite;
  }
  .wave-bar:nth-child(2) { animation-delay: 0.15s; }
  .wave-bar:nth-child(3) { animation-delay: 0.3s; }
  .wave-bar:nth-child(4) { animation-delay: 0.45s; }
  .wave-bar:nth-child(5) { animation-delay: 0.3s; }
  .wave-bar:nth-child(6) { animation-delay: 0.15s; }
  .wave-bar:nth-child(7) { animation-delay: 0s; }
  .wave-bar-paused {
    animation-play-state: paused !important;
    height: 8px !important;
    background-color: #d9d9d9 !important;
  }
  .common-file-preview-modal .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    background-color: #fafafa !important;
    border-color: #f0f0f0 !important;
    color: #595959 !important;
  }
  .common-file-preview-modal .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
    background-color: #ffffff !important;
    color: #1890ff !important;
    border-bottom-color: #ffffff !important;
  }
  .common-file-preview-modal .ant-tabs-content {
    background-color: #ffffff !important;
    border: 1px solid #f0f0f0 !important;
    border-top: none !important;
    border-radius: 0 0 8px 8px;
  }
  /* Tablet & Mobile Header Layout: Title full width so it never cuts off */
  @media (max-width: 992px) {
    .preview-modal-header-content {
      flex-direction: column !important;
      align-items: flex-start !important;
      row-gap: 8px !important;
    }
    .preview-modal-title-wrapper {
      max-width: calc(100% - 28px) !important;
      width: 100% !important;
    }
    .preview-modal-title-text {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: clip !important;
      font-size: 14px !important;
      line-height: 1.4 !important;
    }
    .preview-modal-toolbar {
      width: 100% !important;
      justify-content: flex-start !important;
    }
  }
  /* Mobile Specific Layout: remove redundant horizontal space and fit modal to screen */
  @media (max-width: 576px) {
    .common-file-preview-modal {
      width: calc(100% - 12px) !important;
      max-width: calc(100% - 12px) !important;
      margin: 6px auto !important;
    }
    .common-file-preview-modal .ant-modal-content {
      border-radius: 8px !important;
    }
    .common-file-preview-modal .ant-modal-header {
      padding: 10px 10px !important;
    }
    .common-file-preview-modal .ant-modal-body {
      padding: 4px 0px !important;
      min-height: 40vh !important;
    }
    .preview-checkered-bg {
      border-radius: 0 0 8px 8px !important;
      height: 70vh !important;
    }
  }
`;

const getFileType = (
  fileName?: string,
  url?: string,
  mimeType?: string,
): string => {
  let ext = "";
  if (fileName) {
    ext = fileName.split(".").pop()?.toLowerCase() || "";
  } else if (url) {
    try {
      const pathname = new URL(url).pathname;
      ext = pathname.split(".").pop()?.toLowerCase() || "";
    } catch {
      ext = url.split(".").pop()?.split("?")[0]?.toLowerCase() || "";
    }
  }

  if (mimeType) {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.startsWith("text/") || mimeType === "application/json")
      return "text";
    if (
      mimeType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      mimeType === "application/vnd.ms-excel"
    )
      return "excel";
    if (
      mimeType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      mimeType === "application/msword"
    )
      return "word";
  }

  const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
  const videoExts = ["mp4", "webm", "ogv", "mov", "m4v"];
  const audioExts = ["mp3", "wav", "ogg", "aac", "flac", "m4a"];
  const textExts = [
    "txt",
    "csv",
    "json",
    "log",
    "xml",
    "css",
    "js",
    "ts",
    "tsx",
    "html",
    "md",
  ];
  const excelExts = ["xlsx", "xls", "csv"];
  const docExts = ["doc", "docx", "rtf", "odt"];
  const pptExts = ["ppt", "pptx"];

  if (imageExts.includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (videoExts.includes(ext)) return "video";
  if (audioExts.includes(ext)) return "audio";
  if (textExts.includes(ext)) return "text";
  if (excelExts.includes(ext)) return "excel";
  if (docExts.includes(ext)) return "word";
  if (pptExts.includes(ext)) return "powerpoint";
  return "unknown";
};

const getFileIcon = (type: string) => {
  switch (type) {
    case "image":
      return <FileImageOutlined style={{ color: "#52c41a" }} />;
    case "pdf":
      return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    case "video":
      return <VideoCameraOutlined style={{ color: "#1890ff" }} />;
    case "audio":
      return <CustomerServiceOutlined style={{ color: "#722ed1" }} />;
    case "text":
      return <FileTextOutlined style={{ color: "#fa8c16" }} />;
    case "excel":
      return <FileExcelOutlined style={{ color: "#52c41a" }} />;
    case "word":
    case "powerpoint":
      return <FileWordOutlined style={{ color: "#2f54eb" }} />;
    default:
      return <FileUnknownOutlined style={{ color: "#bfbfbf" }} />;
  }
};

const CommonFilePreviewModal: React.FC<CommonFilePreviewModalProps> = ({
  visible,
  open,
  onClose,
  file,
  files,
  activeIndex = 0,
  onActiveIndexChange,
}) => {
  const [localIndex, setLocalIndex] = useState<number>(0);

  // Normalize files/file inputs
  const resolvedFiles = files || (file ? [file] : []);
  const currentFile = resolvedFiles[localIndex];

  const [resolvedUrl, setResolvedUrl] = useState<string>("");
  const [resolvedName, setResolvedName] = useState<string>("");
  const [resolvedType, setResolvedType] = useState<string>("");

  // Image zoom, rotate, and pan state
  const [scale, setScale] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Excel sheet state
  const [excelSheets, setExcelSheets] = useState<{
    [sheetName: string]: any[][];
  }>({});
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [excelLoading, setExcelLoading] = useState<boolean>(false);

  // Text preview state
  const [textContent, setTextContent] = useState<string>("");
  const [textLoading, setTextLoading] = useState<boolean>(false);

  // Audio play state
  const [audioPlaying, setAudioPlaying] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Keep local index in sync with activeIndex prop
  useEffect(() => {
    if (activeIndex !== undefined) {
      setLocalIndex(activeIndex);
    }
  }, [activeIndex]);

  // Clean up and resolve file details when active file changes
  useEffect(() => {
    let objectUrl = "";
    if (!currentFile) {
      setResolvedUrl("");
      setResolvedName("");
      setResolvedType("");
      return;
    }

    // Reset visual controls
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setExcelSheets({});
    setActiveSheet("");
    setTextContent("");
    setAudioPlaying(false);

    let rawUrl = "";
    let rawName = "";
    let rawType = "";

    if (typeof currentFile === "string") {
      rawUrl = currentFile;
      rawName = currentFile.split("/").pop() || "File Preview";
    } else if (currentFile instanceof File) {
      objectUrl = URL.createObjectURL(currentFile);
      rawUrl = objectUrl;
      rawName = currentFile.name;
      rawType = getFileType(currentFile.name, undefined, currentFile.type);
    } else {
      const item = currentFile as FilePreviewItem;
      if (item.fileObject) {
        objectUrl = URL.createObjectURL(item.fileObject);
        rawUrl = objectUrl;
        rawName = item.name || item.fileObject.name;
        rawType = getFileType(
          item.name || item.fileObject.name,
          undefined,
          item.fileObject.type,
        );
      } else if (item.url) {
        rawUrl = item.url;
        rawName = item.name || item.url.split("/").pop() || "File Preview";
        rawType = getFileType(item.name, item.url, item.type);
      }
    }

    // Automatically resolve relative AWS S3 urls to full URLs
    if (
      rawUrl &&
      !rawUrl.startsWith("http") &&
      !rawUrl.startsWith("blob:") &&
      !rawUrl.startsWith("data:")
    ) {
      rawUrl = getFullAwsUrl(rawUrl);
    }

    // Clean file name to remove raw MongoDB ObjectIDs / S3 prefixes
    const cleanFileName = (name: string) => {
      if (!name) return "File Preview";
      const stripped = name.replace(/^[0-9a-fA-F]{24}_|^[0-9a-fA-F-]{36}_/, "");
      if (/^[0-9a-fA-F]{24}(\.[a-zA-Z0-9]+)?$/.test(stripped)) {
        return "File Preview";
      }
      return stripped || "File Preview";
    };

    setResolvedUrl(rawUrl);
    setResolvedName(cleanFileName(rawName));
    setResolvedType(rawType || getFileType(rawName, rawUrl));

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [currentFile, localIndex]);

  // Load Excel data
  useEffect(() => {
    if (resolvedType === "excel" && resolvedUrl) {
      setExcelLoading(true);
      const loadExcelData = async () => {
        try {
          const response = await fetch(resolvedUrl);
          const data = await response.arrayBuffer();
          const workbook = XLSX.read(data, { type: "array" });
          const sheetsData: { [sheetName: string]: any[][] } = {};
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json<any[]>(worksheet, {
              header: 1,
            });
            sheetsData[sheetName] = json;
          });
          setExcelSheets(sheetsData);
          setActiveSheet(workbook.SheetNames[0] || "");
        } catch (e) {
          console.error("Error reading excel file", e);
          message.error("Failed to parse Excel file.");
        } finally {
          setExcelLoading(false);
        }
      };
      loadExcelData();
    }
  }, [resolvedUrl, resolvedType]);

  // Load text data
  useEffect(() => {
    if (resolvedType === "text" && resolvedUrl) {
      setTextLoading(true);
      const loadTextData = async () => {
        try {
          const response = await fetch(resolvedUrl);
          const text = await response.text();
          setTextContent(text);
        } catch (e) {
          console.error("Error reading text file", e);
          setTextContent("Failed to read file content.");
        } finally {
          setTextLoading(false);
        }
      };
      loadTextData();
    }
  }, [resolvedUrl, resolvedType]);

  // Handlers for image interaction (pan/zoom)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.25, 4));
  const handleZoomOut = () =>
    setScale((prev) => {
      const next = Math.max(prev - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const handleReset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  // Downloader
  const handleDownload = async () => {
    if (!resolvedUrl) return;
    try {
      const response = await fetch(resolvedUrl);
      const blob = await response.blob();

      if (Capacitor.isNativePlatform()) {
        const blobToBase64 = (blob: Blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result.split(",")[1]);
            };

            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const base64Data = await blobToBase64(blob);
          const uniqueFileName = `${Date.now()}_${resolvedName}`;
          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
            title: resolvedName,
          });
        } catch (err) {
          console.error("Native Share Error:", err);
        }
      } else {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = resolvedName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      // Web fallback (CORS or fetch failure)
      if (!Capacitor.isNativePlatform()) {
        const link = document.createElement("a");
        link.href = resolvedUrl;
        link.download = resolvedName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        await Share.share({
          url: resolvedUrl,
          title: resolvedName,
        });
      }
    }
  };

  // Copy text helper
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(textContent);
    message.success("Copied to clipboard!");
  };

  // Navigations
  const handlePrev = () => {
    const nextIndex =
      localIndex > 0 ? localIndex - 1 : resolvedFiles.length - 1;
    setLocalIndex(nextIndex);
    if (onActiveIndexChange) {
      onActiveIndexChange(nextIndex);
    }
  };

  const handleNext = () => {
    const nextIndex =
      localIndex < resolvedFiles.length - 1 ? localIndex + 1 : 0;
    setLocalIndex(nextIndex);
    if (onActiveIndexChange) {
      onActiveIndexChange(nextIndex);
    }
  };

  // Native PDF handler
  const isOpen = open || visible;
  if (Capacitor.isNativePlatform() && resolvedType === "pdf" && isOpen) {
    const pdfBlob =
      currentFile instanceof File
        ? currentFile
        : (currentFile as any)?.fileObject;
    return (
      <CapacitorPdfViewer
        isOpen={isOpen}
        onClose={onClose}
        pdfUrl={resolvedUrl}
        pdfBlob={pdfBlob}
        title={resolvedName}
        fileName={resolvedName}
      />
    );
  }

  // Render main preview area based on file type
  const renderPreviewContent = () => {
    if (!resolvedUrl) {
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center p-5 text-muted"
          style={{ minHeight: "50vh" }}
        >
          <Spin size="large" />
          <span className="mt-3">Resolving file...</span>
        </div>
      );
    }

    switch (resolvedType) {
      case "image":
        return (
          <div
            className="preview-checkered-bg w-100"
            style={{ height: "65vh" }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={resolvedUrl}
              alt={resolvedName}
              draggable={false}
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                transition: isDragging ? "none" : "transform 0.2s ease",
                cursor:
                  scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              }}
            />
          </div>
        );

      case "pdf":
        return (
          <iframe
            src={resolvedUrl}
            title={resolvedName}
            style={{
              width: "100%",
              height: "70vh",
              border: "none",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
            }}
          />
        );

      case "video":
        return (
          <div
            className="d-flex align-items-center justify-content-center w-100"
            style={{
              height: "65vh",
              backgroundColor: "#000",
              borderRadius: "8px",
            }}
          >
            <video
              controls
              autoPlay
              src={resolvedUrl}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        );

      case "audio":
        return (
          <div
            className="d-flex align-items-center justify-content-center w-100"
            style={{ height: "65vh" }}
          >
            <div className="audio-preview-card d-flex flex-column align-items-center justify-content-center p-4 text-center">
              <div
                className={`audio-vinyl-disk mb-4 ${
                  audioPlaying ? "audio-disk-spin" : "audio-disk-paused"
                }`}
              >
                <div className="audio-vinyl-center"></div>
                <CustomerServiceOutlined
                  style={{ fontSize: 60, color: "#fff" }}
                />
              </div>

              <h5
                className="text-dark mb-2"
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: "100%",
                  padding: "0 10px",
                }}
              >
                {resolvedName}
              </h5>

              <div
                className="d-flex justify-content-center align-items-end gap-1 mb-4"
                style={{ height: "40px" }}
              >
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
                <div
                  className={`wave-bar ${
                    !audioPlaying ? "wave-bar-paused" : ""
                  }`}
                ></div>
              </div>

              <audio
                ref={audioRef}
                src={resolvedUrl}
                onPlay={() => setAudioPlaying(true)}
                onPause={() => setAudioPlaying(false)}
                onEnded={() => setAudioPlaying(false)}
                controls
                className="w-100"
                style={{ maxWidth: "400px" }}
              />
            </div>
          </div>
        );

      case "excel":
        return renderExcelContent();

      case "text":
        return (
          <div style={{ position: "relative", height: "100%" }}>
            <div
              className="d-flex gap-2"
              style={{ position: "absolute", right: 12, top: 12, zIndex: 10 }}
            >
              <Button
                icon={<CopyOutlined />}
                size="small"
                style={{
                  backgroundColor: "#fafafa",
                  color: "#333",
                  border: "1px solid #d9d9d9",
                }}
                onClick={handleCopyToClipboard}
              >
                Copy
              </Button>
            </div>
            {textLoading ? (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ minHeight: "400px" }}
              >
                <Spin size="large" tip="Loading text contents..." />
              </div>
            ) : (
              <pre className="code-preview-container">
                {textContent || "Empty file content."}
              </pre>
            )}
          </div>
        );

      default:
        return (
          <div
            className="d-flex flex-column align-items-center justify-content-center p-5 text-center"
            style={{
              height: "55vh",
              backgroundColor: "#fafafa",
              borderRadius: "8px",
            }}
          >
            {getFileIcon(resolvedType)}
            <h4 className="text-dark mt-4">{resolvedName}</h4>
            <p className="text-muted mt-2 mb-4" style={{ maxWidth: "400px" }}>
              Preview is not supported for this file type. You can download the
              file to view it on your device.
            </p>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              size="large"
              style={{ background: "#1890ff", borderColor: "#1890ff" }}
            >
              DOWNLOAD FILE
            </Button>
          </div>
        );
    }
  };

  const renderExcelContent = () => {
    if (excelLoading) {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "50vh" }}
        >
          <Spin size="large" tip="Parsing Excel sheet data..." />
        </div>
      );
    }

    const sheetNames = Object.keys(excelSheets);

    if (sheetNames.length === 0) {
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center p-5 text-center"
          style={{ height: "50vh" }}
        >
          <FileExcelOutlined style={{ fontSize: 48, color: "#faad14" }} />
          <p className="mt-3 text-muted">Unable to parse Excel file preview.</p>
        </div>
      );
    }

    return (
      <div
        style={{
          padding: "8px",
          borderRadius: "8px",
          overflow: "hidden",
          height: "100%",
        }}
      >
        <Tabs
          activeKey={activeSheet}
          onChange={setActiveSheet}
          type="card"
          size="small"
          items={sheetNames.map((sheetName) => {
            const rows = excelSheets[sheetName] || [];
            return {
              key: sheetName,
              label: sheetName,
              children: (
                <div
                  style={{
                    overflow: "auto",
                    maxHeight: "58vh",
                    border: "1px solid #f0f0f0",
                    borderRadius: "4px",
                  }}
                >
                  <table className="excel-preview-table">
                    <tbody>
                      {rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => {
                            const isHeader = rowIndex === 0;
                            return isHeader ? (
                              <th key={cellIndex}>
                                {cell !== null && cell !== undefined
                                  ? String(cell)
                                  : ""}
                              </th>
                            ) : (
                              <td key={cellIndex}>
                                {cell !== null && cell !== undefined
                                  ? String(cell)
                                  : ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ),
            };
          })}
        />
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: cssStyles }} />
      <Modal
        open={isOpen}
        title={
          <div
            className="preview-modal-header-content d-flex flex-wrap align-items-center justify-content-between pe-2 pe-md-4 w-100"
            style={{ rowGap: "8px", columnGap: "8px" }}
          >
            <div
              className="preview-modal-title-wrapper d-flex align-items-center gap-2 text-dark flex-shrink-1"
              style={{ maxWidth: "60%", minWidth: 0 }}
            >
              {getFileIcon(resolvedType)}
              <span
                className="preview-modal-title-text"
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
                title={resolvedName}
              >
                {resolvedName}
              </span>
            </div>

            {/* Toolbar in the middle/right */}
            <div className="preview-modal-toolbar d-flex align-items-center justify-content-end flex-wrap gap-1 gap-md-2">
              {resolvedFiles.length > 1 && (
                <div
                  className="d-flex align-items-center gap-1 me-1 me-md-2 flex-shrink-0"
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={
                      <LeftOutlined
                        style={{ color: "#333", fontSize: "12px" }}
                      />
                    }
                    onClick={handlePrev}
                    style={{ width: "24px", height: "24px", minWidth: "24px" }}
                  />
                  <span
                    className="text-dark"
                    style={{
                      fontSize: "11px",
                      minWidth: "32px",
                      textAlign: "center",
                    }}
                  >
                    {localIndex + 1} / {resolvedFiles.length}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    icon={
                      <RightOutlined
                        style={{ color: "#333", fontSize: "12px" }}
                      />
                    }
                    onClick={handleNext}
                    style={{ width: "24px", height: "24px", minWidth: "24px" }}
                  />
                </div>
              )}

              {resolvedType === "image" && (
                <div className="d-flex gap-1 me-1 me-md-2 flex-shrink-0">
                  <Tooltip title="Zoom In">
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <ZoomInOutlined
                          style={{ color: "#333", fontSize: "14px" }}
                        />
                      }
                      onClick={handleZoomIn}
                      style={{
                        width: "28px",
                        height: "28px",
                        minWidth: "28px",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Zoom Out">
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <ZoomOutOutlined
                          style={{ color: "#333", fontSize: "14px" }}
                        />
                      }
                      disabled={scale <= 0.5}
                      onClick={handleZoomOut}
                      style={{
                        width: "28px",
                        height: "28px",
                        minWidth: "28px",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Rotate">
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <RotateRightOutlined
                          style={{ color: "#333", fontSize: "14px" }}
                        />
                      }
                      onClick={handleRotate}
                      style={{
                        width: "28px",
                        height: "28px",
                        minWidth: "28px",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Reset">
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <UndoOutlined
                          style={{ color: "#333", fontSize: "14px" }}
                        />
                      }
                      onClick={handleReset}
                      style={{
                        width: "28px",
                        height: "28px",
                        minWidth: "28px",
                      }}
                    />
                  </Tooltip>
                </div>
              )}

              <Tooltip title="Download File">
                <Button
                  type="text"
                  size="small"
                  icon={
                    <DownloadOutlined
                      style={{ color: "#333", fontSize: "14px" }}
                    />
                  }
                  onClick={handleDownload}
                  className="flex-shrink-0"
                  style={{ width: "28px", height: "28px", minWidth: "28px" }}
                />
              </Tooltip>
            </div>
          </div>
        }
        footer={null}
        onCancel={onClose}
        closeIcon={<CloseOutlined style={{ color: "#333" }} />}
        width="90%"
        centered
        className="common-file-preview-modal"
        styles={{
          body: {
            padding: "16px 24px 24px",
            minHeight: "50vh",
            backgroundColor: "#ffffff",
          },
        }}
      >
        {renderPreviewContent()}
      </Modal>
    </>
  );
};

export default CommonFilePreviewModal;
